(() => {
  const root = window.Shopify?.routes?.root || "/";

  const cartTargets = () => document.querySelectorAll(
    "[data-cart-count], .cart-bubble__text-count, .nb-cart-count"
  );

  const ensureCartCount = () => {
    const cartLink = document.querySelector(
      '.header a[href*="/cart"], .header-actions a[href*="/cart"], a.header-actions__action[href*="/cart"]'
    );
    if (!cartLink || cartLink.querySelector(".nb-cart-count")) return;
    cartLink.classList.add("nb-cart-link");
    const count = document.createElement("span");
    count.className = "nb-cart-count";
    count.setAttribute("aria-label", "פריטים בעגלה");
    count.textContent = "0";
    cartLink.append(count);
  };

  const paintCount = (count) => {
    ensureCartCount();
    cartTargets().forEach((target) => {
      target.textContent = String(count);
      target.toggleAttribute("hidden", count === 0);
    });
  };

  const refreshCart = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("cart-refresh");
    const cart = await response.json();
    paintCount(cart.item_count);
    return cart;
  };

  const notifyTheme = (cart, sections = {}) => {
    document.dispatchEvent(new CustomEvent("cart:update", {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: "noa-product-grid",
        data: {
          source: "noa-product-grid",
          itemCount: cart.item_count,
          sections,
        },
      },
    }));
  };

  const bindCartForms = () => {
    document.querySelectorAll("[data-nb-cart-form]").forEach((form) => {
      if (form.dataset.nbBound) return;
      form.dataset.nbBound = "true";
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const button = form.querySelector("button[type='submit']");
        const label = button?.querySelector("span");
        const status = form.querySelector("[data-nb-cart-status]");
        if (!button || button.disabled) return;

        button.disabled = true;
        button.classList.add("is-loading");
        if (label) label.textContent = "מוסיף...";
        if (status) status.textContent = "";

        try {
          const payload = new FormData(form);
          const sectionIds = Array.from(document.querySelectorAll("cart-items-component"))
            .map((item) => item.dataset.sectionId)
            .filter(Boolean);
          if (sectionIds.length) {
            payload.append("sections", [...new Set(sectionIds)].join(","));
            payload.append("sections_url", window.location.pathname);
          }

          const response = await fetch(`${root}cart/add.js`, {
            method: "POST",
            headers: { Accept: "application/json" },
            body: payload,
          });
          if (!response.ok) throw new Error("cart-add");
          const added = await response.json();
          const cart = await refreshCart();
          notifyTheme(cart, added.sections || {});
          if (label) label.textContent = "נוסף לעגלה";
          if (status) status.textContent = `יש ${cart.item_count} פריטים בעגלה`;
          window.setTimeout(() => {
            if (label) label.textContent = button.dataset.defaultLabel || "הוספה לעגלה";
            if (status) status.textContent = "";
          }, 1800);
        } catch (_) {
          if (label) label.textContent = button.dataset.defaultLabel || "הוספה לעגלה";
          if (status) status.textContent = "לא הצלחנו להוסיף. נסו שוב.";
        } finally {
          button.disabled = false;
          button.classList.remove("is-loading");
        }
      });
    });
  };

  const bindCarousels = () => {
    document.querySelectorAll("[data-nb-carousel]").forEach((carousel) => {
      if (carousel.dataset.nbBound) return;
      carousel.dataset.nbBound = "true";
      const track = carousel.querySelector("[data-nb-carousel-track]");
      if (!track) return;
      const move = (direction) => track.scrollBy({ left: direction * track.clientWidth, behavior: "smooth" });
      carousel.querySelector("[data-nb-carousel-prev]")?.addEventListener("click", () => move(-1));
      carousel.querySelector("[data-nb-carousel-next]")?.addEventListener("click", () => move(1));

      const originals = Array.from(track.children);
      if (originals.length < 2 || matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      const clones = originals.map((item) => {
        const clone = item.cloneNode(true);
        clone.removeAttribute("id");
        clone.removeAttribute("data-shopify-editor-block");
        clone.setAttribute("aria-hidden", "true");
        track.appendChild(clone);
        return clone;
      });

      track.dataset.nbAutoplay = "true";
      let paused = false;
      let lastFrame = performance.now();
      let loopWidth = 0;
      const measure = () => {
        loopWidth = clones[0].offsetLeft - originals[0].offsetLeft;
      };
      measure();

      const pause = () => { paused = true; };
      const resume = () => {
        paused = false;
        lastFrame = performance.now();
      };
      carousel.addEventListener("pointerenter", pause);
      carousel.addEventListener("pointerleave", resume);
      carousel.addEventListener("pointerdown", pause);
      carousel.addEventListener("pointerup", resume);
      carousel.addEventListener("pointercancel", resume);
      carousel.addEventListener("focusin", pause);
      carousel.addEventListener("focusout", (event) => {
        if (!carousel.contains(event.relatedTarget)) resume();
      });
      addEventListener("resize", measure, { passive: true });

      const tick = (now) => {
        if (!carousel.isConnected) return;
        if (!paused && !document.hidden && loopWidth > 0) {
          const elapsed = Math.min(now - lastFrame, 64);
          track.scrollLeft += elapsed * 0.035;
          if (track.scrollLeft >= loopWidth) track.scrollLeft -= loopWidth;
        }
        lastFrame = now;
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  };

  const bindReveals = () => {
    const items = document.querySelectorAll(".nb-reveal, .nb-reveal-stagger > *");
    if (!items.length || matchMedia("(prefers-reduced-motion: reduce)").matches) {
      items.forEach((item) => item.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -40px" });
    items.forEach((item, index) => {
      item.style.setProperty("--nb-delay", `${Math.min(index % 6, 5) * 70}ms`);
      observer.observe(item);
    });
  };

  const translateFacetLabels = (scope = document) => {
    const labels = { Price: "מחיר", Availability: "מלאי" };
    scope.querySelectorAll?.(".facets__label").forEach((label) => {
      const translated = labels[label.textContent.trim()];
      if (translated) label.textContent = translated;
    });
  };

  const watchFacetLabels = () => {
    translateFacetLabels();
    const facets = document.querySelector("facets-form, .facets, #FacetFiltersForm");
    if (!facets || facets.dataset.noaTranslationObserver) return;
    facets.dataset.noaTranslationObserver = "true";
    new MutationObserver(() => translateFacetLabels(facets)).observe(facets, {
      childList: true,
      subtree: true,
    });
  };

  const quizPackPrices = [199, 350, 450];
  const updateQuizPrices = () => {
    document.querySelectorAll(".nq-md").forEach((modal) => {
      const rows = [...modal.querySelectorAll(".nq-q")].slice(0, 3);
      rows.forEach((row, index) => {
        const price = quizPackPrices[index];
        const perBottle = Math.round(price / (index + 1));
        const saving = quizPackPrices[0] * (index + 1) - price;
        const total = row.querySelector(".nq-qp b");
        const per = row.querySelector(".nq-qp i");
        if (total && total.textContent !== `₪${price}`) total.textContent = `₪${price}`;
        if (per && per.textContent !== `₪${perBottle} לבקבוק`) per.textContent = `₪${perBottle} לבקבוק`;
        row.querySelectorAll(".nq-sv, .nq-qs").forEach((item) => {
          const next = item.textContent.replace(/חיסכון ₪[\d,]+/, `חיסכון ₪${saving}`);
          if (next !== item.textContent) item.textContent = next;
        });
      });
      const addon = modal.querySelector(".nq-adp");
      if (addon && addon.textContent !== "+₪199") addon.textContent = "+₪199";
      const selected = rows.findIndex((row) => row.classList.contains("nq-sel"));
      const cta = modal.querySelector(".nq-cta");
      if (cta && selected >= 0) {
        const total = quizPackPrices[selected] + (modal.querySelector(".nq-ad.on") ? 199 : 0);
        const next = cta.textContent.replace(/₪[\d,]+/, `₪${total}`);
        if (next !== cta.textContent) cta.textContent = next;
      }
    });
  };

  const watchQuizPrices = () => {
    updateQuizPrices();
    if (document.documentElement.dataset.noaQuizPriceObserver) return;
    document.documentElement.dataset.noaQuizPriceObserver = "true";
    new MutationObserver(updateQuizPrices).observe(document.body, { childList: true, subtree: true });
    document.addEventListener("click", () => setTimeout(updateQuizPrices), true);
  };

  const replaceFreeShippingCopy = (scope = document.body) => {
    if (!scope) return;
    const replaceNode = (node) => {
      const current = node.nodeValue || "";
      if (!current.includes("המשלוח יחושב בקופה")) return;
      node.nodeValue = current.replaceAll("המשלוח יחושב בקופה", "המשלוח חינם");
    };
    if (scope.nodeType === Node.TEXT_NODE) {
      replaceNode(scope);
      return;
    }
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) replaceNode(node);
  };

  const watchFreeShippingCopy = () => {
    replaceFreeShippingCopy();
    if (document.documentElement.dataset.noaFreeShippingObserver) return;
    document.documentElement.dataset.noaFreeShippingObserver = "true";
    new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === "characterData") replaceFreeShippingCopy(record.target);
        record.addedNodes?.forEach((node) => replaceFreeShippingCopy(node));
      });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  const ensureBreadcrumbs = () => {
    const rootUrl = new URL(root, window.location.origin);
    const rootPath = rootUrl.pathname.replace(/\/+$/, "") || "/";
    const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
    if (currentPath === rootPath || document.querySelector(".noa-breadcrumbs")) return;

    const main = document.querySelector("main");
    if (!main) return;

    const relativePath = currentPath
      .slice(rootPath === "/" ? 0 : rootPath.length)
      .replace(/^\/+/, "");
    const parts = relativePath.split("/").filter(Boolean);
    const pageType = parts[0] || "";
    const absolute = (path) => new URL(path.replace(/^\/+/, ""), rootUrl).href;
    const titleFromDocument = document.title
      .replace(/\s+(?:[–—-]|\|)\s+NOA.*$/i, "")
      .trim();
    const visibleHeading = document.querySelector("main h1")?.textContent?.replace(/\s+/g, " ").trim();
    const currentTitle = titleFromDocument || visibleHeading || "NOA";
    const items = [{ name: "דף הבית", url: rootUrl.href }];

    if (pageType === "products" || (pageType === "collections" && parts[2] === "products")) {
      items.push({ name: "כל המוצרים", url: absolute("collections/all") });
      items.push({ name: currentTitle });
    } else if (pageType === "blogs" && parts.length > 2) {
      items.push({ name: "מגזין NOA", url: absolute(`blogs/${parts[1]}`) });
      items.push({ name: currentTitle });
    } else {
      const labels = {
        cart: "סל הקניות",
        search: "חיפוש",
        account: "החשבון שלי",
        policies: currentTitle,
        collections: currentTitle,
        pages: currentTitle,
        blogs: "מגזין NOA",
      };
      items.push({ name: labels[pageType] || currentTitle });
    }

    const style = document.createElement("style");
    style.id = "noa-breadcrumbs-style";
    style.textContent = `
      .noa-breadcrumbs{width:min(100%,1280px);margin:0 auto;padding:18px clamp(16px,4vw,48px) 0;box-sizing:border-box;color:rgb(var(--color-foreground-rgb)/.62);font:650 13px/1.5 var(--nb-font,inherit)}
      .noa-breadcrumbs__list{display:flex;align-items:center;gap:8px;margin:0;padding:0;overflow-x:auto;list-style:none;white-space:nowrap;scrollbar-width:none}
      .noa-breadcrumbs__list::-webkit-scrollbar{display:none}
      .noa-breadcrumbs__item{display:flex;align-items:center;min-width:0}
      .noa-breadcrumbs__item+.noa-breadcrumbs__item::before{content:"›";margin-inline-end:8px;color:rgb(var(--color-foreground-rgb)/.35);font-size:17px;line-height:1}
      .noa-breadcrumbs a{color:inherit;text-decoration:none;transition:color .18s ease}
      .noa-breadcrumbs a:hover{color:rgb(var(--color-foreground-rgb));text-decoration:underline;text-underline-offset:3px}
      .noa-breadcrumbs [aria-current="page"]{display:block;overflow:hidden;color:rgb(var(--color-foreground-rgb)/.82);text-overflow:ellipsis}
      @media(max-width:749px){.noa-breadcrumbs{padding-block-start:12px;font-size:12px}.noa-breadcrumbs__list{gap:6px}.noa-breadcrumbs__item+.noa-breadcrumbs__item::before{margin-inline-end:6px}}
    `;
    document.head.appendChild(style);

    const nav = document.createElement("nav");
    nav.className = "noa-breadcrumbs";
    nav.dir = "rtl";
    nav.setAttribute("aria-label", "פירורי לחם");
    const list = document.createElement("ol");
    list.className = "noa-breadcrumbs__list";
    items.forEach((item) => {
      const entry = document.createElement("li");
      entry.className = "noa-breadcrumbs__item";
      if (item.url) {
        const link = document.createElement("a");
        link.href = item.url;
        link.textContent = item.name;
        entry.appendChild(link);
      } else {
        const current = document.createElement("span");
        current.setAttribute("aria-current", "page");
        current.textContent = item.name;
        entry.appendChild(current);
      }
      list.appendChild(entry);
    });
    nav.appendChild(list);
    main.insertAdjacentElement("afterbegin", nav);

    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.dataset.noaBreadcrumbsSchema = "true";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url || document.querySelector('link[rel="canonical"]')?.href || window.location.href,
      })),
    });
    document.head.appendChild(schema);
  };

  const init = () => {
    ensureCartCount();
    refreshCart().catch(() => {});
    bindCartForms();
    bindCarousels();
    bindReveals();
    watchFacetLabels();
    watchQuizPrices();
    watchFreeShippingCopy();
    ensureBreadcrumbs();
  };

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", init, { once: true })
    : init();
  document.addEventListener("shopify:section:load", init);
})();
